/**
 * Shared Location Tags management body (#999 Stories 1.2 / 3.12 · QA #5–#8/#11).
 * Hosted by LocationTagsManagementModal — web centered dialog, native full-screen.
 */

import { Pencil, Trash2, X } from 'lucide-react-native';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import { useOptionalLocationTagsDraft } from '../../contexts/LocationTagsDraftContext';
import { useCreateLocationTag } from '../../hooks/mutations/useCreateLocationTag';
import { useDeleteLocationTag } from '../../hooks/mutations/useDeleteLocationTag';
import { useUpdateLocationTag } from '../../hooks/mutations/useUpdateLocationTag';
import { useLocationTags } from '../../hooks/queries/useLocationTags';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { ApiError } from '../../services/api';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { lineHeight } from '../../theme/typography';
import { compareLocationTagCreationOrder } from '../../utils/groupUnitsByLocationThenStatus';
import { showToast } from '../../utils/toast';

import type { LocationTag } from '../../types/locationTag';

export type LocationTagsManagementLayout = 'dialog' | 'fullscreen';

export interface LocationTagsManagementScreenProps {
  /** When false, queries pause and edit state resets. */
  active: boolean;
  onClose: () => void;
  requestId: string;
  canMutate: boolean;
  /** A transient reload must disable actions without discarding typed rename state. */
  permissionsLoading?: boolean;
  mode?: 'server' | 'draft';
  layout?: LocationTagsManagementLayout;
  /**
   * When false, host supplies chrome (native Header). Web dialog keeps local title/close.
   */
  showLocalChrome?: boolean;
}

type FeedbackTone = 'success' | 'error';

type FeedbackState = {
  tone: FeedbackTone;
  message: string;
};

function sortTags(tags: LocationTag[]): LocationTag[] {
  return [...tags].sort((a, b) => compareLocationTagCreationOrder(a.id, b.id));
}

export const LocationTagsManagementScreen: React.FC<
  LocationTagsManagementScreenProps
> = ({
  active,
  onClose,
  requestId,
  canMutate,
  permissionsLoading = false,
  mode = 'server',
  layout = 'dialog',
  showLocalChrome,
}) => {
  const draftContext = useOptionalLocationTagsDraft();
  const isDraftMode = mode === 'draft' && draftContext != null;
  const isFullscreen = layout === 'fullscreen';
  const localChrome = showLocalChrome ?? !isFullscreen;

  const {
    data: serverTags = [],
    isLoading: isServerLoading,
    isError: isServerError,
    refetch,
  } = useLocationTags(requestId, active && !isDraftMode);
  const createTag = useCreateLocationTag();
  const updateTag = useUpdateLocationTag();
  const deleteTag = useDeleteLocationTag();
  const { confirm, confirmationDialog } = useConfirmationDialog({
    inline: true,
  });

  const tags: LocationTag[] = isDraftMode
    ? draftContext.draftTags.map(draft => ({
        id: draft.clientId,
        requestId: 0,
        name: draft.name,
      }))
    : serverTags;

  const isLoading = !isDraftMode && isServerLoading;
  const isError = !isDraftMode && isServerError;

  const [newTagName, setNewTagName] = useState('');
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const canMutateRef = useRef(canMutate);
  canMutateRef.current = canMutate;
  const previousCanMutateRef = useRef(canMutate);

  const sortedTags = useMemo(() => sortTags(tags), [tags]);

  useEffect(() => {
    if (!active) {
      setNewTagName('');
      setEditingTagId(null);
      setEditingName('');
      setFeedback(null);
    }
  }, [active]);

  useEffect(() => {
    if (permissionsLoading) {
      return;
    }
    if (previousCanMutateRef.current && !canMutate) {
      setEditingTagId(null);
      setEditingName('');
    }
    previousCanMutateRef.current = canMutate;
  }, [canMutate, permissionsLoading]);

  const notifySuccess = useCallback((message: string) => {
    setFeedback({ tone: 'success', message });
    if (Platform.OS === 'web') {
      showToast.success(message);
    }
  }, []);

  const notifyError = useCallback((message: string) => {
    setFeedback({ tone: 'error', message });
    if (Platform.OS === 'web') {
      showToast.error(message);
    }
  }, []);

  const handleCreate = useCallback(() => {
    const trimmed = newTagName.trim();
    if (!trimmed || !canMutate) {
      return;
    }

    if (isDraftMode && draftContext) {
      try {
        draftContext.addDraftTag(trimmed);
        setNewTagName('');
        notifySuccess('Location tag created');
      } catch (error) {
        notifyError(
          error instanceof Error
            ? error.message
            : 'Failed to create location tag',
        );
      }
      return;
    }

    createTag.mutate(
      { requestId, data: { name: trimmed } },
      {
        onSuccess: () => {
          setNewTagName('');
          notifySuccess('Location tag created');
        },
        onError: error => {
          const message =
            error instanceof ApiError
              ? error.message
              : 'Failed to create location tag';
          notifyError(message);
        },
      },
    );
  }, [
    canMutate,
    createTag,
    draftContext,
    isDraftMode,
    newTagName,
    notifyError,
    notifySuccess,
    requestId,
  ]);

  const startEditing = useCallback(
    (tag: LocationTag) => {
      if (!tag.id || !canMutate) {
        return;
      }
      setEditingTagId(tag.id);
      setEditingName(tag.name);
    },
    [canMutate],
  );

  const cancelEditing = useCallback(() => {
    setEditingTagId(null);
    setEditingName('');
  }, []);

  const handleRename = useCallback(() => {
    if (editingTagId == null || !canMutate) {
      return;
    }
    const trimmed = editingName.trim();
    if (!trimmed) {
      notifyError('Location tag name cannot be empty');
      return;
    }

    if (isDraftMode && draftContext) {
      try {
        draftContext.renameDraftTag(editingTagId, trimmed);
        cancelEditing();
        notifySuccess('Location tag renamed');
      } catch (error) {
        notifyError(
          error instanceof Error
            ? error.message
            : 'Failed to rename location tag',
        );
      }
      return;
    }

    updateTag.mutate(
      {
        requestId,
        tagId: editingTagId,
        data: { name: trimmed },
      },
      {
        onSuccess: () => {
          cancelEditing();
          notifySuccess('Location tag renamed');
        },
        onError: error => {
          const message =
            error instanceof ApiError
              ? error.message
              : 'Failed to rename location tag';
          notifyError(message);
        },
      },
    );
  }, [
    canMutate,
    cancelEditing,
    editingName,
    editingTagId,
    draftContext,
    isDraftMode,
    notifyError,
    notifySuccess,
    requestId,
    updateTag,
  ]);

  const handleDelete = useCallback(
    async (tag: LocationTag) => {
      if (!tag.id || !canMutate) {
        return;
      }

      const confirmed = await confirm({
        title: 'Delete location tag?',
        message:
          'This removes the tag from the request. Tags assigned to equipment cannot be deleted.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonStyle: 'danger',
      });
      if (!confirmed || !canMutateRef.current) {
        return;
      }

      if (isDraftMode && draftContext) {
        try {
          draftContext.deleteDraftTag(tag.id);
          notifySuccess('Location tag deleted');
        } catch (error) {
          notifyError(
            error instanceof Error
              ? error.message
              : 'Failed to delete location tag',
          );
        }
        return;
      }

      deleteTag.mutate(
        { requestId, tagId: tag.id },
        {
          onSuccess: () => {
            notifySuccess('Location tag deleted');
          },
          onError: error => {
            const message =
              error instanceof ApiError
                ? error.message
                : 'Failed to delete location tag';
            notifyError(message);
          },
        },
      );
    },
    [
      canMutate,
      confirm,
      deleteTag,
      draftContext,
      isDraftMode,
      notifyError,
      notifySuccess,
      requestId,
    ],
  );

  const isSaving =
    createTag.isPending || updateTag.isPending || deleteTag.isPending;

  return (
    <View
      style={[styles.root, isFullscreen && styles.rootFullscreen]}
      testID={
        isFullscreen
          ? 'pd-location-tags-fullscreen'
          : 'pd-location-tags-dialog-body'
      }
    >
      {localChrome ? (
        <View style={styles.header}>
          <Text style={styles.title}>Location Tags</Text>
          <TouchableOpacity
            onPress={onClose}
            testID="pd-location-tags-modal-close"
            accessibilityRole="button"
            accessibilityLabel="Close location tags"
            style={styles.closeButton}
          >
            <X size={20} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      ) : null}

      <Text style={styles.subtitle}>
        {canMutate
          ? 'Create labels for rooms and areas, then assign them to equipment.'
          : 'View location labels for this request.'}
      </Text>

      {feedback ? (
        <View
          style={[
            styles.feedbackBanner,
            feedback.tone === 'success'
              ? styles.feedbackSuccess
              : styles.feedbackError,
          ]}
          testID={
            feedback.tone === 'success'
              ? 'pd-location-tags-feedback-success'
              : 'pd-location-tags-feedback-error'
          }
          accessibilityRole="alert"
        >
          <Text
            style={[
              styles.feedbackText,
              feedback.tone === 'success'
                ? styles.feedbackSuccessText
                : styles.feedbackErrorText,
            ]}
          >
            {feedback.message}
          </Text>
          <TouchableOpacity
            onPress={() => setFeedback(null)}
            accessibilityRole="button"
            accessibilityLabel="Dismiss feedback"
            testID="pd-location-tags-feedback-dismiss"
            style={styles.feedbackDismiss}
          >
            <X
              size={16}
              color={
                feedback.tone === 'success' ? colors.success : colors.error
              }
            />
          </TouchableOpacity>
        </View>
      ) : null}

      {isLoading ? (
        <View style={styles.centered}>
          <ActivityIndicator color={colors.primary} />
        </View>
      ) : isError ? (
        <View style={styles.centered}>
          <Text style={styles.errorText}>Could not load location tags.</Text>
          <TouchableOpacity
            onPress={() => refetch()}
            style={styles.retryButton}
            testID="pd-location-tags-retry"
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.bodyColumn}>
          <ScrollView
            style={[
              styles.listScroll,
              isFullscreen && styles.listScrollFullscreen,
            ]}
            contentContainerStyle={styles.listContent}
            keyboardShouldPersistTaps="handled"
            testID="pd-location-tags-list-scroll"
          >
            {sortedTags.length === 0 ? (
              <Text style={styles.emptyText} testID="pd-location-tags-empty">
                No location tags yet.
              </Text>
            ) : (
              sortedTags.map(tag => {
                const isEditing = editingTagId === tag.id;

                return (
                  <View
                    key={tag.id ?? tag.name}
                    style={styles.tagRow}
                    testID={`pd-location-tag-row-${tag.id}`}
                  >
                    {isEditing ? (
                      <TextInput
                        value={editingName}
                        onChangeText={setEditingName}
                        style={styles.tagInput}
                        autoFocus
                        testID={`pd-location-tag-edit-input-${tag.id}`}
                        accessibilityLabel="Edit location tag name"
                      />
                    ) : (
                      <Text
                        style={[
                          styles.tagName,
                          !canMutate && styles.readOnlyText,
                        ]}
                      >
                        {tag.name}
                      </Text>
                    )}

                    <View style={styles.tagActions}>
                      {isEditing ? (
                        <>
                          <TouchableOpacity
                            onPress={handleRename}
                            disabled={isSaving || !canMutate}
                            style={[
                              styles.actionButton,
                              !canMutate && styles.mutationDisabled,
                            ]}
                            testID={`pd-location-tag-save-${tag.id}`}
                            accessibilityRole="button"
                            accessibilityLabel="Save location tag name"
                            accessibilityState={{
                              disabled: isSaving || !canMutate,
                            }}
                          >
                            <Text style={styles.saveText}>Save</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={cancelEditing}
                            disabled={isSaving}
                            style={styles.actionButton}
                            testID={`pd-location-tag-cancel-${tag.id}`}
                            accessibilityRole="button"
                            accessibilityLabel="Cancel rename"
                          >
                            <Text style={styles.cancelText}>Cancel</Text>
                          </TouchableOpacity>
                        </>
                      ) : (
                        <>
                          <TouchableOpacity
                            onPress={() => startEditing(tag)}
                            disabled={isSaving || !canMutate}
                            style={[
                              styles.iconButton,
                              !canMutate && styles.mutationDisabled,
                            ]}
                            testID={`pd-location-tag-rename-${tag.id}`}
                            accessibilityRole="button"
                            accessibilityLabel={`Rename ${tag.name}`}
                            accessibilityState={{
                              disabled: isSaving || !canMutate,
                            }}
                          >
                            <Pencil
                              size={16}
                              color={
                                canMutate ? colors.primary : colors.textMuted
                              }
                            />
                          </TouchableOpacity>
                          <TouchableOpacity
                            onPress={() => {
                              handleDelete(tag).catch(() => undefined);
                            }}
                            disabled={isSaving || !canMutate}
                            style={[
                              styles.iconButton,
                              !canMutate && styles.mutationDisabled,
                            ]}
                            testID={`pd-location-tag-delete-${tag.id}`}
                            accessibilityRole="button"
                            accessibilityLabel={`Delete ${tag.name}`}
                            accessibilityState={{
                              disabled: isSaving || !canMutate,
                            }}
                          >
                            <Trash2
                              size={16}
                              color={
                                canMutate ? colors.error : colors.textMuted
                              }
                            />
                          </TouchableOpacity>
                        </>
                      )}
                    </View>
                  </View>
                );
              })
            )}
          </ScrollView>

          <View style={styles.createRow} testID="pd-location-tags-create-row">
            <TextInput
              value={newTagName}
              onChangeText={setNewTagName}
              placeholder="New location tag"
              style={[styles.createInput, !canMutate && styles.readOnlyInput]}
              testID="pd-location-tag-create-input"
              accessibilityLabel="New location tag name"
              editable={canMutate && !isSaving}
              accessibilityState={{ disabled: !canMutate || isSaving }}
            />
            <TouchableOpacity
              onPress={handleCreate}
              disabled={
                !canMutate || isSaving || newTagName.trim().length === 0
              }
              style={[
                styles.createButton,
                (!canMutate || isSaving || newTagName.trim().length === 0) &&
                  styles.createButtonDisabled,
              ]}
              testID="pd-location-tag-create-button"
              accessibilityRole="button"
              accessibilityState={{
                disabled:
                  !canMutate || isSaving || newTagName.trim().length === 0,
              }}
              accessibilityLabel="Add location tag"
            >
              <Text style={styles.createButtonText}>Add</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {active ? confirmationDialog : null}
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    width: '100%',
    maxHeight: '100%',
    minHeight: 0,
    ...Platform.select({
      web: {
        display: 'flex' as unknown as undefined,
        flexDirection: 'column' as unknown as undefined,
        flex: 1,
      },
      default: {},
    }),
  },
  rootFullscreen: {
    flex: 1,
    backgroundColor: colors.backgroundPrimary,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
    minHeight: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: lineHeight.body,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  closeButton: {
    padding: spacing.xs,
  },
  feedbackBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  feedbackSuccess: {
    backgroundColor: '#ecfdf5',
    borderColor: colors.success,
  },
  feedbackError: {
    backgroundColor: '#fef2f2',
    borderColor: colors.error,
  },
  feedbackText: {
    flex: 1,
    fontSize: 14,
    lineHeight: lineHeight.body,
  },
  feedbackSuccessText: {
    color: colors.success,
  },
  feedbackErrorText: {
    color: colors.error,
  },
  feedbackDismiss: {
    padding: spacing.xs,
  },
  centered: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl,
    gap: spacing.sm,
    flex: 1,
  },
  errorText: {
    color: colors.error,
    textAlign: 'center',
  },
  retryButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  retryButtonText: {
    color: colors.primary,
    fontWeight: '600',
  },
  bodyColumn: {
    flex: 1,
    minHeight: 0,
    ...Platform.select({
      web: {
        display: 'flex' as unknown as undefined,
        flexDirection: 'column' as unknown as undefined,
      },
      default: {},
    }),
  },
  listScroll: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minHeight: 120,
  },
  listScrollFullscreen: {
    flex: 1,
    minHeight: 0,
  },
  listContent: {
    gap: spacing.sm,
    paddingBottom: spacing.sm,
  },
  emptyText: {
    color: colors.textSecondary,
    textAlign: 'center',
    paddingVertical: spacing.lg,
  },
  tagRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: 8,
    backgroundColor: colors.backgroundSecondary,
  },
  tagName: {
    flex: 1,
    fontSize: 15,
    color: colors.textPrimary,
  },
  tagInput: {
    flex: 1,
    fontSize: 15,
    color: colors.textPrimary,
    borderBottomWidth: 1,
    borderBottomColor: colors.primary,
    paddingVertical: 2,
    ...Platform.select({
      web: {
        outlineStyle: 'none' as unknown as undefined,
      },
    }),
  },
  tagActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  actionButton: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  iconButton: {
    padding: spacing.xs,
  },
  mutationDisabled: {
    opacity: 0.45,
  },
  readOnlyText: {
    color: colors.textMuted,
  },
  readOnlyInput: {
    opacity: 0.55,
  },
  saveText: {
    color: colors.primary,
    fontWeight: '600',
  },
  cancelText: {
    color: colors.textSecondary,
  },
  createRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.borderMedium,
    paddingTop: spacing.md,
    flexShrink: 0,
  },
  createInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: 15,
    color: colors.textPrimary,
    ...Platform.select({
      web: {
        outlineStyle: 'none' as unknown as undefined,
      },
    }),
  },
  createButton: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  createButtonDisabled: {
    opacity: 0.5,
  },
  createButtonText: {
    color: colors.textLight,
    fontWeight: '600',
  },
});
